import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { LookupItem } from "../models/LookupItem";
import { ViewModeItem, ViewModeItems } from "../models/ViewModeItem";
import { AvailableSortItems, SortItem } from "../models/SortItem";
import { NvkbItem } from "../models/NvkbItem";
import { FacetResult } from "../models/FacetResult";
import { SuggestItem } from "../models/SuggestItem";
import { SuggestFilter } from '../models/common/SuggestFilter';

export interface NVKBState {
  viewMode: ViewModeItem;
  orderBy: SortItem;
  keywords: string;
  suggestions: SuggestItem[];
  selectedCategory: undefined | LookupItem;
  selectedBranch: undefined | LookupItem;
  isLoading: boolean;
  isSuggestLoading: boolean;
  canLoadMore: boolean;
  items: NvkbItem[];
  pageSize: number;
  page: number;
  initDone: boolean;
}

const initialState: NVKBState = {
  viewMode: ViewModeItems[0],
  orderBy: AvailableSortItems[1],
  keywords: "",
  suggestions: [],
  selectedCategory: undefined,
  selectedBranch: undefined,
  isLoading: false,
  isSuggestLoading: false,
  canLoadMore: false,
  items: [],
  pageSize: 9,
  page: 1,
  initDone: false,
};

export type SearchFilter = {
  keywords?: string;
  category?: string;
  branch?: string;
  path?: string;
  page: number;
  pageSize: number;
};

export const searchNvkbSuggest = createAsyncThunk<SuggestItem[], SuggestFilter>(
  "data/searchNvkbSuggest",
  async (payload: SuggestFilter): Promise<SuggestItem[]> => {
    const response = await fetch("/nvkbsearch/suggest", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    return (await response.json()) as SuggestItem[];
  }
);
export const searchNvkb = createAsyncThunk<FacetResult<NvkbItem>, SearchFilter>(
  "data/searchNvkb",
  async (payload: SearchFilter): Promise<FacetResult<NvkbItem>> => {
    const response = await fetch("/nvkbsearch", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error("Failed to fetch data");
    }
    return (await response.json()) as FacetResult<NvkbItem>;
  }
);

export const nvkbSlice = createSlice({
  name: "nvkb",
  initialState,
  reducers: {
    changePage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    changeViewMode: (state, action: PayloadAction<ViewModeItem>) => {
      state.viewMode = action.payload;
    },
    changeCategory: (state, action: PayloadAction<undefined | LookupItem>) => {
      if (state.selectedCategory?.value !== action.payload?.value) {
        state.items = [];
        state.canLoadMore = false;
      }
      state.selectedCategory = action.payload;
      state.page = 1;
    },
    changeBranch: (state, action: PayloadAction<undefined | LookupItem>) => {
      if (state.selectedBranch?.value !== action.payload?.value) {
        state.items = [];
        state.canLoadMore = false;
      }
      state.selectedBranch = action.payload;
      state.page = 1;
    },
    changeKeywords: (state, action: PayloadAction<string>) => {
      if (state.keywords!== action.payload)
        {
          state.items = [];
          state.canLoadMore = false;
        }
      state.keywords = action.payload;
      state.page = 1;
    },
    clearFilters: (state) => {
      state.selectedCategory = undefined;
      state.selectedBranch = undefined;
      state.items = [];
      state.page = 1;
      state.canLoadMore = false;
    },
    changeInit: (state, action: PayloadAction<boolean>) => {
      state.initDone = action.payload;
    },
    clearSuggestions: (state) => {
      state.suggestions = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(searchNvkb.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(
        searchNvkb.fulfilled,
        (state, action: PayloadAction<FacetResult<NvkbItem>>) => {
          state.isLoading = false;

          if (action.payload) {
            if (state.page == 1) {
              state.items = [];
            }
            if (action.payload.hits.length > 0) {
              state.items.push(...action.payload.hits);
            }

            state.canLoadMore =
              action.payload.totalHits > state.items.length &&
              state.items.length > 0;
          }
        }
      )
      .addCase(searchNvkb.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(searchNvkbSuggest.pending, (state) => {
        state.isSuggestLoading = true;
      })
      .addCase(
        searchNvkbSuggest.fulfilled,
        (state, action: PayloadAction<SuggestItem[]>) => {
          state.isSuggestLoading = false;

          state.suggestions = [];

          if (action.payload.length > 0) {
            state.suggestions = [...action.payload];
          }
        }
      )
      .addCase(searchNvkbSuggest.rejected, (state) => {
        state.isSuggestLoading = false;
      });
  },
});

export const {
  changeCategory,
  changeBranch,
  changeKeywords,
  changePage,
  changeViewMode,
  clearFilters,
  changeInit,
  clearSuggestions
} = nvkbSlice.actions;

export default nvkbSlice.reducer;
